/**
 * Dynamic Product Card
 *
 * Infrastructure (from the rate-limit-hardened build):
 *  - Lazy fetch: only request a card once it is near the viewport (IntersectionObserver).
 *  - Concurrency limit: at most MAX_CONCURRENT section requests run in parallel.
 *  - In-flight/result cache: identical URLs share a single request.
 *  - 429 handling: respects Retry-After and backs off with exponential retry.
 *  - AbortController: cancels the request if the element is removed before it resolves.
 *
 * Features (merged from the swatch build):
 *  - Desktop hover preview on color swatches.
 *  - Swatch title / subtitle updates.
 *  - "+N" swatch expander (mobile redirect / desktop inline expand).
 *  - Breakpoint-aware swatch overflow recalculation on resize.
 */

const SECTION_ID = 'product-card-single';
const MAX_CONCURRENT = 4;
const MAX_RETRIES = 3;
const BASE_BACKOFF_MS = 800;
const OBSERVER_ROOT_MARGIN = '300px'; // start fetching a bit before the card is visible

// --- Shared request scheduler -------------------------------------------------

const requestQueue = [];
let activeRequests = 0;

// Cache keyed by URL. Stores the pending/resolved promise so duplicate handles
// (same product shown twice on a page) only trigger one network request.
const cardCache = new Map();

function schedule(task) {
  return new Promise((resolve, reject) => {
    requestQueue.push({ task, resolve, reject });
    drainQueue();
  });
}

function drainQueue() {
  while (activeRequests < MAX_CONCURRENT && requestQueue.length > 0) {
    const { task, resolve, reject } = requestQueue.shift();
    activeRequests += 1;
    task()
      .then(resolve, reject)
      .finally(() => {
        activeRequests -= 1;
        drainQueue();
      });
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function fetchSectionHtml(url, signal) {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    const response = await fetch(url, { signal });

    if (response.ok) {
      return response.text();
    }

    // Rate limited: respect Retry-After if present, otherwise exponential backoff.
    if (response.status === 429 && attempt < MAX_RETRIES) {
      const retryAfter = parseFloat(response.headers.get('Retry-After'));
      const waitMs = Number.isFinite(retryAfter)
        ? retryAfter * 1000
        : BASE_BACKOFF_MS * Math.pow(2, attempt) + Math.random() * 300;
      await sleep(waitMs);
      continue;
    }

    throw new Error(`Failed to load product card (${response.status}) for ${url}`);
  }

  throw new Error(`Exhausted retries loading product card for ${url}`);
}

function loadCard(url, signal) {
  if (cardCache.has(url)) {
    return cardCache.get(url);
  }

  const promise = schedule(() => fetchSectionHtml(url, signal)).catch((error) => {
    // Do not cache failures, so the card can be retried on next intersection.
    cardCache.delete(url);
    throw error;
  });

  cardCache.set(url, promise);
  return promise;
}

// --- Shared lazy-load observer -----------------------------------------------

const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        observer.unobserve(entry.target);
        entry.target.render();
      }
    });
  },
  { rootMargin: OBSERVER_ROOT_MARGIN },
);

// --- Custom element ----------------------------------------------------------

class DynamicProductCard extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.hasRendered = false;
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    const handle = this.getAttribute('handle');
    if (!handle) return;
    // Defer the fetch until the card is near the viewport.
    observer.observe(this);
  }

  disconnectedCallback() {
    observer.unobserve(this);
    // Cancel an in-flight request if the card leaves the DOM early.
    this.abortController.abort();
    // Drop the breakpoint listener (safe even if it was never attached).
    window.removeEventListener('resize', this._onResize);
  }

  _onResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      this.querySelectorAll('.swatch-picker').forEach((wrapper) => {
        this.updateSwatchOverflow(wrapper);
      });
    }, 150);
  }

  async render() {
    if (this.hasRendered) return;
    this.hasRendered = true;

    const handle = this.getAttribute('handle');
    if (!handle) return;

    const url = `/products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`; 

    try {
      const html = await loadCard(url, this.abortController.signal);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const wrapper = doc.querySelector('.product-card-wrapper');

      if (!wrapper) {
        throw new Error(`.product-card-wrapper not found in section for ${handle}`);
      }

      this.appendChild(wrapper);
      this.initColorSwitcher();
      this.initSwatchExpand();
      this.initMediaSwipe();

      // Recalculate visible/hidden swatches on resize (mobile <-> desktop breakpoint).
      // Attached only after a successful render so cards that never render stay clean.
      window.addEventListener('resize', this._onResize);
    } catch (error) {
      // Ignore aborts (element removed / navigated away); surface real failures.
      if (error.name === 'AbortError') return;
      console.error(error);
      this.innerHTML = `<p style="color:red;">Failed to load product card</p>`;
    }
  }

  initColorSwitcher() {
    // Product card - color switcher
    const colorSwitchers = this.querySelectorAll(
      '.product-variant-picker-color .product-variant-color-picker-link',
    );

    if (!colorSwitchers) return;

    let hoverTimer = null;

    const updateCardData = (switcher) => {
      // Get the closest parent .product-card-wrapper element of the clicked button
      const gridItem = switcher.closest('.product-card-wrapper');

      // Update title
      const newTitle = switcher.getAttribute('data-display-title') || switcher.getAttribute('data-title');
      if (newTitle) gridItem.querySelector('.card__heading__wrapper a').textContent = newTitle;

      // Update color name
      const newColor = switcher.getAttribute('data-color-name');
      if (newColor) gridItem.querySelector('.product-description-metafield').textContent = newColor;

      // Update occasion
      const newOccasion = switcher.getAttribute('data-occasion');
      const occasionEl = gridItem.querySelector('.product-best-for-metafield');
      if (occasionEl) occasionEl.textContent = newOccasion || '';

      // Update swatch title
      const newSwatchTitle = switcher.getAttribute('data-swatch-title');
      const newSwatchTitleColor = switcher.getAttribute('data-swatch-title-color');
      const newSwatchTitleFont = switcher.getAttribute('data-swatch-title-font');
      const newSwatchTitleFontSize = switcher.getAttribute('data-swatch-title-font-size');
      const newSwatchTitleFontSizeDesktop = switcher.getAttribute('data-swatch-title-font-size-desktop');

      const swatchTitleEl = gridItem.querySelector('.card-information .text-swatch__title');

      if (swatchTitleEl) {
        swatchTitleEl.textContent = newSwatchTitle || '';

        swatchTitleEl.style.setProperty('--title-color', newSwatchTitleColor || '');
        swatchTitleEl.style.setProperty('--title-font', newSwatchTitleFont || '');
        swatchTitleEl.style.setProperty('--title-size', newSwatchTitleFontSize || '');
        swatchTitleEl.style.setProperty('--title-size-desktop', newSwatchTitleFontSizeDesktop || '');
      }

      // Update swatch subtitle
      const newSwatchSubtitle = switcher.getAttribute('data-swatch-subtitle');
      const newSwatchSubtitleColor = switcher.getAttribute('data-swatch-subtitle-color');
      const newSwatchSubtitleFont = switcher.getAttribute('data-swatch-subtitle-font');
      const newSwatchSubtitleFontSize = switcher.getAttribute('data-swatch-subtitle-font-size');
      const newSwatchSubtitleFontSizeDesktop = switcher.getAttribute('data-swatch-subtitle-font-size-desktop');

      const swatchSubtitleEl = gridItem.querySelector('.card-information .text-swatch__subtitle');

      if (swatchSubtitleEl) {
        swatchSubtitleEl.textContent = newSwatchSubtitle || '';

        swatchSubtitleEl.style.setProperty('--subtitle-color', newSwatchSubtitleColor || '');
        swatchSubtitleEl.style.setProperty('--subtitle-font', newSwatchSubtitleFont || '');
        swatchSubtitleEl.style.setProperty('--subtitle-size', newSwatchSubtitleFontSize || '');
        swatchSubtitleEl.style.setProperty('--subtitle-size-desktop', newSwatchSubtitleFontSizeDesktop || '');
      }

      // Update badge
      const newBadgeCopy = switcher.getAttribute('data-tag-type');
      const newBadgeClass = switcher.getAttribute('data-badge-class');
      if (newBadgeCopy && newBadgeClass) {
        const newBadge = `<span class="${newBadgeClass}">${newBadgeCopy}</span>`;
        gridItem.querySelector('.card__badge').innerHTML = newBadge;
      } else gridItem.querySelector('.card__badge').innerHTML = '';

      // Update the compare price
      const newComparePrice = switcher.getAttribute('data-compare-price');
      const newPrice = switcher.getAttribute('data-price');

      if (newComparePrice) {
        gridItem.querySelector('.price').classList.add('price--on-sale');
        const comparePriceElement = gridItem?.querySelector(
          '.price__sale .price-item--regular',
        );
        const regularPriceElement = gridItem?.querySelector(
          '.price__sale .price-item--sale',
        );
        if (regularPriceElement) {
          regularPriceElement.textContent = newPrice;
        }
        if (comparePriceElement) {
          comparePriceElement.textContent = newComparePrice;
        }
      } else {
        gridItem.querySelector('.price').classList.remove('price--on-sale');
        const priceElement = gridItem?.querySelector(
          '.price__regular .price-item--regular',
        );
        if (priceElement) {
          priceElement.textContent = newPrice;
        }
      }

      // Update the product URL
      const productLinks = gridItem?.querySelectorAll('.card__heading a');
      const newProductUrl = switcher.getAttribute('data-product-url');
      if (productLinks) {
        productLinks.forEach((productLink) => {
          productLink.href = newProductUrl;
        });
      }

      const newProductId = switcher.getAttribute('data-product-id');
      this.updateProductsImages(newProductId);

      // Handle 'active' class for the clicked button
      gridItem
        .querySelectorAll('.product-variant-color-picker-link')
        .forEach((link) => {
          link.classList.remove('active');
          link.ariaSelected = null;
        });
      switcher.classList.add('active');
      switcher.ariaSelected = 'true';

      // Let the card's Quick View ("+") and size bar follow the selected color.
      gridItem.dispatchEvent(
        new CustomEvent('card:colorchange', {
          bubbles: true,
          detail: {productId: newProductId, productUrl: newProductUrl},
        }),
      );

      window.dispatchEvent(new CustomEvent('abra:render'));
    };

    colorSwitchers.forEach((switcher) => {
      switcher.addEventListener('click', (event) => {
        event.preventDefault(); // Prevent the button from redirecting immediately
        clearTimeout(hoverTimer);
        updateCardData(switcher);
        this.trackSwatchSelect(switcher);
      });

      switcher.addEventListener('mouseenter', () => {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        if (!isDesktop) return;

        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => {
          updateCardData(switcher);
        }, 80);
      });
    });
  }

  initSwatchExpand() {
    // Product card - "+N" swatch expander (product-variant-swatch-picker.liquid)
    // Behavior differs by breakpoint:
    //   - Mobile (<1024px): "+N" redirects to this card's PDP
    //   - Desktop (>=1024px): "+N" expands to reveal all swatches inline
    const wrappers = this.querySelectorAll('.swatch-picker');

    wrappers.forEach((wrapper) => {
      this.updateSwatchOverflow(wrapper);

      // const moreButton = wrapper.querySelector('.swatch-picker__more');
      // if (!moreButton) return;

      // moreButton.addEventListener('click', (event) => {
      //   event.preventDefault();
      //   event.stopPropagation();

      //   const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

      //   if (isDesktop) {
      //     wrapper.classList.add('is-expanded');
      //     moreButton.setAttribute('aria-expanded', 'true');
      //   } else {
      //     const productUrl = moreButton.getAttribute('data-product-url');
      //     if (productUrl) window.location.href = productUrl;
      //   }
      // });
    });
  }

  updateSwatchOverflow(wrapper) {
    // Recalculates how many dots are hidden for the current breakpoint
    // and syncs the "+N" chip's visibility/label accordingly.
    const dots = wrapper.querySelectorAll('.product-variant-color-picker-link');
    const moreButton = wrapper.querySelector('.swatch-picker__more');
    if (!moreButton) return;

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const visibleCount = isDesktop
      ? parseInt(wrapper.dataset.swatchCountDesktop || '9', 10)
      : parseInt(wrapper.dataset.swatchCountMobile || '6', 10);

    const hiddenCount = dots.length - visibleCount;

    if (hiddenCount > 0) {
      moreButton.hidden = false;
      moreButton.querySelector('.swatch-picker__more-count').textContent = `+${hiddenCount}`;
    } else {
      moreButton.hidden = true;
      moreButton.setAttribute('aria-expanded', 'false');
      wrapper.classList.remove('is-expanded');
    }
  }

  initMediaSwipe() {
    // Mobile PGP image swipe (base.css), PGP grid only: the image track sits above the
    // card link overlay so it can be swiped, so a tap on it has to open the PDP itself.
    // Native scrolling doesn't fire a click, so this only runs on a tap.
    if (!this.closest('.nosto-grid-container')) return;
    this.querySelectorAll('.media--swipe').forEach((track) => {
      track.addEventListener('click', () => {
        const link = this.querySelector('.card__heading a');
        if (link) window.location.href = link.href;
      });
    });
  }

  updateProductsImages(id) {
    const images = this.querySelectorAll('.card__media');
    images.forEach((img) => img.classList.add('hidden'));
    const currentImage = this.querySelector(`.card__media[data-id="${id}"]`);
    // Guard against a missing image node to avoid a runtime TypeError.
    if (currentImage) currentImage.classList.remove('hidden');

    // Start the swipe carousel from the first image for the newly selected color.
    const track = currentImage?.querySelector('.media--swipe');
    if (track) track.scrollLeft = 0;
  }

  trackSwatchSelect(switcher) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'dl_select_color_swatch',
      ecommerce: {
        items: [{
          item_id:      'shopify_US_' + switcher.getAttribute('data-product-id'),
          item_name:    switcher.getAttribute('data-title'),
          item_variant: switcher.getAttribute('data-color-name'),
          price: parseFloat((switcher.getAttribute('data-price') || '').replace(/[^0-9.]/g, '')) || 0
        }]
      }
    });
  }
}

customElements.define('dynamic-product-card', DynamicProductCard);